import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, memo, useEffect, useMemo, useRef, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef/index.ts';
import { SortOrder, ITEM_MIN_HEIGHT_WITH_BORDER, ITEM_MIN_HEIGHT, areOptionsEqual, keepAfterBulkRemove, } from '../../Components/SelectionList/index.js';
import AdditionalMessage from '../../Feedback/AdditionalMessage/index.js';
import { List, Search } from './components/index.js';
import { DEFAULT_LOAD_DEBOUNCE, DEFAULT_OVERFLOW_LIMIT, INLINE_VALUE_DELIMITER, DEFAULT_TRANSLATIONS } from './constants.js';
import { filterOptionsByString, sortOptions, createOptionValueSet, filterOptionsExcluding, getOptionText, matchInlineTokens, splitInlineValue, toMatchKey, } from './helpers.js';
import { StyledTextarea, Wrapper } from './styled.js';
export const MultiSelection = memo(forwardRef(function MultiSelection({ selectedValue, onChange, options: staticOptions = [], loadOptions, isLoading, disabled, error, initialSelectionSortOrder = SortOrder.ASC, initialSelectedSortOrder = SortOrder.ASC, overflowLimit = DEFAULT_OVERFLOW_LIMIT, getOptionKey, formatOptionLabel, getOptionText: getOptionTextFn, translations, hideInlineToggle = false, hideBulkSelect = false, hideBulkDeselect = false, maxItems, reverseSelectionMode = false, getOptionAliases, resolveInlineTokens, onInlineResolvingChange, dataTestId, }, ref) {
    const [fetchedOptions, setFetchedOptions] = useState([]);
    const [internalLoading, setInternalLoading] = useState(!!loadOptions);
    const [selectionFilter, setSelectionFilter] = useState('');
    const [selectedFilter, setSelectedFilter] = useState('');
    const [isInlineMode, setIsInlineMode] = useState(false);
    const [inlineValue, setInlineValue] = useState('');
    const [unresolvedTokens, setUnresolvedTokens] = useState([]);
    const [unavailableOptions, setUnavailableOptions] = useState([]);
    const [isResolvingInline, setIsResolvingInline] = useState(false);
    // The textarea's text as of now, for a lookup that comes back after it changed.
    const inlineValueRef = useRef('');
    // Counts the lookups asked for; one that comes back after a later one was asked for, or after the
    // textarea closed, has nothing left to answer.
    const inlineLookupIdRef = useRef(0);
    const isResolvingInlineRef = useRef(false);
    // What the textarea can name beyond the loaded page: the selection it opened with, and every option a
    // lookup returned since.
    const knownInlineOptionsRef = useRef([]);
    // The values the textarea opened with: a disabled one among them was the user's to keep, so erasing
    // and retyping it puts it back.
    const openedWithValuesRef = useRef(new Set());
    // Entries a lookup already answered with nothing, so typing on does not ask for them again.
    const notFoundKeysRef = useRef(new Set());
    // The value last handed to `onChange`, so a change made from outside can be told apart from one's own.
    const emittedValueRef = useRef(selectedValue);
    const [selectionSortOrder, setSelectionSortOrder] = useState(initialSelectionSortOrder);
    const [selectedSortOrder, setSelectedSortOrder] = useState(initialSelectedSortOrder);
    const selectionListRef = useRef(null);
    const [textareaHeight, setTextareaHeight] = useState(0);
    const listContainerHeight = useMemo(() => ITEM_MIN_HEIGHT_WITH_BORDER * overflowLimit + ITEM_MIN_HEIGHT / 2, [overflowLimit]);
    const mergedTranslations = useMemo(() => ({
        ...DEFAULT_TRANSLATIONS,
        ...translations,
    }), [translations]);
    const hasLoadOptions = useMemo(() => !!loadOptions, [loadOptions]);
    const options = useMemo(() => (hasLoadOptions ? fetchedOptions : staticOptions), [fetchedOptions, staticOptions, hasLoadOptions]);
    const isSelectionFilterDisabled = useMemo(() => (hasLoadOptions ? disabled : isLoading || disabled), [hasLoadOptions, isLoading, disabled]);
    const availableSelectionItems = useMemo(() => {
        const selectedValueSet = createOptionValueSet(selectedValue ?? []);
        return filterOptionsExcluding(options, selectedValueSet);
    }, [options, selectedValue]);
    const filteredSelectionItems = useMemo(() => {
        if (isInlineMode || hasLoadOptions) {
            // For async options, don't apply client-side sorting - server handles it
            return availableSelectionItems;
        }
        const filteredItems = filterOptionsByString(availableSelectionItems, selectionFilter, formatOptionLabel, getOptionTextFn);
        return sortOptions(filteredItems, selectionSortOrder, formatOptionLabel, getOptionTextFn);
    }, [isInlineMode, hasLoadOptions, availableSelectionItems, selectionFilter, formatOptionLabel, selectionSortOrder, getOptionTextFn]);
    const selectableSelectionItems = useMemo(() => filteredSelectionItems.filter((option) => !option.disabled), [filteredSelectionItems]);
    const filteredSelectedItems = useMemo(() => {
        const filteredItems = filterOptionsByString(selectedValue, selectedFilter, formatOptionLabel, getOptionTextFn);
        return sortOptions(filteredItems, selectedSortOrder, formatOptionLabel, getOptionTextFn);
    }, [selectedValue, selectedFilter, formatOptionLabel, selectedSortOrder, getOptionTextFn]);
    const loadOptionsDebounced = useDebouncedCallback(async (searchString, newSortOrder) => {
        if (!loadOptions)
            return;
        setInternalLoading(true);
        try {
            const options = await loadOptions(searchString, newSortOrder ?? selectionSortOrder);
            setFetchedOptions(options || []);
        }
        catch {
            setFetchedOptions([]);
        }
        finally {
            setInternalLoading(false);
        }
    }, DEFAULT_LOAD_DEBOUNCE);
    const handleSelectionFilterChange = useCallbackRef((value) => {
        setSelectionFilter(value);
        if (hasLoadOptions) {
            loadOptionsDebounced(value);
        }
    });
    const handleSelectedFilterChange = useCallbackRef((value) => {
        setSelectedFilter(value);
    });
    const clearSelectionFilter = useCallbackRef(() => {
        setSelectionFilter('');
        if (hasLoadOptions) {
            loadOptionsDebounced('');
        }
    });
    const clearSelectedFilter = useCallbackRef(() => {
        setSelectedFilter('');
    });
    const emitChange = useCallbackRef((value) => {
        emittedValueRef.current = value;
        onChange(value);
    });
    const writeInlineValue = useCallbackRef((value) => {
        inlineValueRef.current = value;
        setInlineValue(value);
    });
    const getInlineText = useCallbackRef((values) => values.map((option) => getOptionText(option, formatOptionLabel, getOptionTextFn)).join(INLINE_VALUE_DELIMITER));
    // The selection counts as a source too: with `loadOptions` it is rarely all on the loaded page, and an
    // entry only the page could match would drop a selected value on the next keystroke. A disabled option
    // is one the list would not let the user pick, so typing it does not pick it either — unless the
    // selection held it, which the list does not let the user undo. An entry naming one is reported as
    // unavailable rather than as not found: the option exists, and a lookup would only find it again.
    const matchInlineValue = useCallbackRef((value, previousSelection = []) => {
        const knownOptions = [...options, ...knownInlineOptionsRef.current];
        const isPickable = (option) => !option.disabled || openedWithValuesRef.current.has(option.value);
        const { matched, unmatched } = matchInlineTokens(splitInlineValue(value), [...knownOptions.filter(isPickable), ...selectedValue, ...previousSelection], formatOptionLabel, getOptionTextFn, getOptionAliases);
        const unavailable = matchInlineTokens(unmatched, knownOptions.filter((option) => !isPickable(option)), formatOptionLabel, getOptionTextFn, getOptionAliases);
        return { matched, unmatched: unavailable.unmatched, unavailable: unavailable.matched };
    });
    const getValuesNamedInText = useCallbackRef((text, candidates) => createOptionValueSet(matchInlineTokens(splitInlineValue(text), candidates, formatOptionLabel, getOptionTextFn, getOptionAliases).matched));
    // A fixed option offers no remove button, so the text does not take it out either: an edit that leaves
    // its entry unnamed — a Backspace at its end, say — would turn it into a stray entry and bring the
    // option back as a second one. Only the options the text named before count: one it cannot name at all
    // would otherwise refuse every keystroke.
    const dropsFixedEntry = useCallbackRef((value) => {
        const fixedOptions = selectedValue.filter((option) => option.fixed);
        const namedValues = getValuesNamedInText(value, fixedOptions);
        return [...getValuesNamedInText(inlineValueRef.current, fixedOptions)].some((fixedValue) => !namedValues.has(fixedValue));
    });
    // From the moment a lookup is asked for, not from when the debounce lets it go: a form saved in between
    // would save the selection without what the lookup is about to add.
    const setResolvingInline = useCallbackRef((resolving) => {
        if (isResolvingInlineRef.current === resolving)
            return;
        isResolvingInlineRef.current = resolving;
        setIsResolvingInline(resolving);
        onInlineResolvingChange?.(resolving);
    });
    const resolveInlineValueDebounced = useDebouncedCallback(async (lookupId, tokens) => {
        if (!resolveInlineTokens)
            return;
        let resolvedOptions = [];
        let hasLookupFailed = false;
        try {
            resolvedOptions = await resolveInlineTokens(tokens);
        }
        catch {
            // A failed lookup resolves nothing, so its entries are listed as not found — but only for now.
            hasLookupFailed = true;
        }
        if (lookupId !== inlineLookupIdRef.current)
            return;
        const knownValues = createOptionValueSet(knownInlineOptionsRef.current);
        knownInlineOptionsRef.current = [
            ...knownInlineOptionsRef.current,
            ...resolvedOptions.filter((option) => !knownValues.has(option.value)),
        ];
        // Matched against the text as it is now, which may have grown by a value added from outside since.
        const { matched, unmatched, unavailable } = matchInlineValue(inlineValueRef.current);
        if (!hasLookupFailed) {
            const unmatchedKeys = new Set(unmatched.map(toMatchKey));
            tokens.filter((token) => unmatchedKeys.has(toMatchKey(token))).forEach((token) => notFoundKeysRef.current.add(toMatchKey(token)));
        }
        emitChange(matched);
        setUnresolvedTokens(unmatched);
        setUnavailableOptions(unavailable);
        setResolvingInline(false);
    }, DEFAULT_LOAD_DEBOUNCE);
    const cancelInlineLookup = useCallbackRef(() => {
        inlineLookupIdRef.current += 1;
        resolveInlineValueDebounced.cancel();
        setResolvingInline(false);
    });
    // An edit made through the list rewrites the text from the value, but the entries nothing matched yet
    // stay in it — still waiting on a lookup, or already reported — so the lookup goes on and nothing
    // pasted is lost. The selection before the edit still names what was just removed, so its entry goes
    // rather than lingering as unmatched.
    const rewriteInlineValueKeepingUnmatched = useCallbackRef((values, previousSelection = []) => {
        const { unmatched, unavailable } = matchInlineValue(inlineValueRef.current, previousSelection);
        writeInlineValue([getInlineText(values), ...unmatched, getInlineText(unavailable)].filter(Boolean).join(INLINE_VALUE_DELIMITER));
    });
    // Text written from the value alone: nothing is left to look up, and nothing to report.
    const resetInlineValue = useCallbackRef((values) => {
        cancelInlineLookup();
        setUnresolvedTokens([]);
        setUnavailableOptions([]);
        writeInlineValue(getInlineText(values));
    });
    // Opening or closing the textarea starts over.
    const toggleInlineMode = useCallbackRef(() => {
        if (!isInlineMode && selectionListRef.current) {
            setTextareaHeight(selectionListRef.current.offsetHeight);
        }
        knownInlineOptionsRef.current = selectedValue;
        openedWithValuesRef.current = createOptionValueSet(selectedValue);
        notFoundKeysRef.current = new Set();
        emittedValueRef.current = selectedValue;
        setIsInlineMode((prev) => !prev);
        resetInlineValue(selectedValue);
    });
    const handleChangeStringValue = useCallbackRef((e) => {
        const { value } = e.currentTarget;
        // Refused by leaving the state alone: the textarea is controlled, so it goes back to the text it had.
        if (dropsFixedEntry(value))
            return;
        writeInlineValue(value);
        const { matched, unmatched, unavailable } = matchInlineValue(value);
        emitChange(matched);
        // The lists under the textarea come with the lookup; without one there is nothing to report.
        if (!resolveInlineTokens) {
            cancelInlineLookup();
            setUnresolvedTokens([]);
            return;
        }
        setUnavailableOptions(unavailable);
        // An entry a lookup already answered with nothing is listed straight away instead of asked for again.
        const tokensToLookUp = unmatched.filter((token) => !notFoundKeysRef.current.has(toMatchKey(token)));
        if (tokensToLookUp.length === 0) {
            cancelInlineLookup();
            setUnresolvedTokens(unmatched);
            return;
        }
        // An entry already reported stays listed while it is still unmatched; new ones join once looked up.
        setUnresolvedTokens((previous) => unmatched.filter((token) => previous.includes(token) || notFoundKeysRef.current.has(toMatchKey(token))));
        inlineLookupIdRef.current += 1;
        setResolvingInline(true);
        resolveInlineValueDebounced(inlineLookupIdRef.current, tokensToLookUp);
    });
    const isMaxReached = maxItems !== undefined && selectedValue.length >= maxItems;
    const selectItem = useCallbackRef((option) => {
        if (isMaxReached)
            return;
        emitChange([...selectedValue, option]);
    });
    const unselectItem = useCallbackRef((option) => {
        const filteredValue = selectedValue.filter((value) => !areOptionsEqual(option, value));
        if (isInlineMode) {
            rewriteInlineValueKeepingUnmatched(filteredValue);
        }
        emitChange(filteredValue);
    });
    const selectAll = useCallbackRef(() => {
        const itemsToAdd = maxItems !== undefined ? selectableSelectionItems.slice(0, maxItems - selectedValue.length) : selectableSelectionItems;
        emitChange([...selectedValue, ...itemsToAdd]);
    });
    const unselectAll = useCallbackRef(() => {
        const keptValue = keepAfterBulkRemove(selectedValue, filteredSelectedItems);
        // Clearing clears the pasted entries too, lookup and all; a fixed option survives the removal, so the
        // text keeps it rather than dropping it on the next keystroke.
        if (isInlineMode) {
            resetInlineValue(keptValue);
        }
        emitChange(keptValue);
    });
    const toggleSelectionSortOrder = useCallbackRef(() => {
        setSelectionSortOrder((prev) => {
            const newSortOrder = prev === SortOrder.ASC ? SortOrder.DESC : SortOrder.ASC;
            if (hasLoadOptions) {
                loadOptionsDebounced(selectionFilter, newSortOrder);
            }
            return newSortOrder;
        });
    });
    const toggleSelectedSortOrder = useCallbackRef(() => {
        setSelectedSortOrder((prev) => (prev === SortOrder.ASC ? SortOrder.DESC : SortOrder.ASC));
    });
    useEffect(() => {
        if (hasLoadOptions) {
            loadOptionsDebounced('');
        }
    }, [loadOptionsDebounced, hasLoadOptions]);
    // A lookup still running when the field goes away must neither land in it nor leave the consumer waiting.
    useEffect(() => () => cancelInlineLookup(), [cancelInlineLookup]);
    // A value changed from outside while the textarea is open — options a consumer adds on its own once
    // a request returns, say — is written into it, since the next keystroke parses the text and would
    // drop it again. Compared by value: a form library may hand the same selection back as a new array.
    useEffect(() => {
        const emittedValues = createOptionValueSet(emittedValueRef.current);
        const currentValues = createOptionValueSet(selectedValue);
        const previousSelection = emittedValueRef.current;
        const addedOptions = selectedValue.filter((option) => !emittedValues.has(option.value));
        const hasRemovedOptions = previousSelection.some((option) => !currentValues.has(option.value));
        emittedValueRef.current = selectedValue;
        if (!isInlineMode || (addedOptions.length === 0 && !hasRemovedOptions))
            return;
        // A removal leaves no entry to strike out reliably, so the text is rebuilt from the value.
        if (hasRemovedOptions) {
            rewriteInlineValueKeepingUnmatched(selectedValue, previousSelection);
            return;
        }
        const typedText = inlineValueRef.current.replace(/[\s,]+$/u, '');
        // An option the text already names — typed before anything could match it — is not written twice.
        const namedValues = getValuesNamedInText(typedText, addedOptions);
        const text = [typedText, getInlineText(addedOptions.filter((option) => !namedValues.has(option.value)))]
            .filter(Boolean)
            .join(INLINE_VALUE_DELIMITER);
        writeInlineValue(text);
        // What arrived from outside may answer entries listed as not found or unavailable.
        const { unmatched } = matchInlineValue(text);
        setUnresolvedTokens((previous) => previous.filter((token) => unmatched.includes(token)));
        setUnavailableOptions((previous) => previous.filter((option) => !currentValues.has(option.value)));
    }, [
        selectedValue,
        isInlineMode,
        writeInlineValue,
        rewriteInlineValueKeepingUnmatched,
        getInlineText,
        getValuesNamedInText,
        matchInlineValue,
    ]);
    return (_jsxs(Wrapper, { ref: ref, "data-testid": dataTestId, children: [_jsxs("div", { children: [_jsx(Search, { mode: 'selection', onDebouncedChange: handleSelectionFilterChange, onClear: clearSelectionFilter, disabled: isSelectionFilterDisabled, isInlineMode: isInlineMode, toggleInlineMode: toggleInlineMode, hideInlineToggle: hideInlineToggle, disableInlineToggle: disabled, translations: mergedTranslations, hasLoadOptions: hasLoadOptions }), isInlineMode ? (_jsxs(_Fragment, { children: [_jsx(StyledTextarea, { value: inlineValue, onChange: handleChangeStringValue, height: textareaHeight, disabled: disabled, isLoading: isResolvingInline, warning: unresolvedTokens.length > 0 || unavailableOptions.length > 0, "data-testid": 'inline-textarea' }), unresolvedTokens.length > 0 && (_jsx(AdditionalMessage, { message: `${mergedTranslations.notFound}: ${unresolvedTokens.join(INLINE_VALUE_DELIMITER)}` })), unavailableOptions.length > 0 && (_jsx(AdditionalMessage, { message: `${mergedTranslations.unavailable}: ${getInlineText(unavailableOptions)}` }))] })) : (_jsx(List, { mode: reverseSelectionMode ? 'selected' : 'selection', options: filteredSelectionItems, total: availableSelectionItems.length, singleAction: selectItem, bulkAction: selectAll, hideBulkActions: hideBulkSelect, isLoading: isLoading || internalLoading, disabled: disabled || isMaxReached || selectableSelectionItems.length === 0, sortOrder: selectionSortOrder, toggleSortOrder: toggleSelectionSortOrder, containerHeight: listContainerHeight, getOptionKey: getOptionKey, formatOptionLabel: formatOptionLabel, translations: mergedTranslations, wrapperRef: selectionListRef }))] }), _jsxs("div", { children: [_jsx(Search, { mode: 'selected', onDebouncedChange: handleSelectedFilterChange, onClear: clearSelectedFilter, disabled: disabled, translations: mergedTranslations, hasLoadOptions: false }), _jsx(List, { mode: reverseSelectionMode ? 'selection' : 'selected', options: filteredSelectedItems, total: selectedValue.length, singleAction: unselectItem, bulkAction: unselectAll, hideBulkActions: hideBulkDeselect, disabled: disabled || filteredSelectedItems.length === 0, error: error, sortOrder: selectedSortOrder, toggleSortOrder: toggleSelectedSortOrder, containerHeight: listContainerHeight, getOptionKey: getOptionKey, formatOptionLabel: formatOptionLabel, translations: mergedTranslations })] })] }));
}));
